// Nova / Editar tag no padrão Aeros Dark (mockup "Modal Tag A"): nome com
// prévia do chip, cor por swatches + hex + conta-gotas (ColorBox), bloco
// "Usar como coluna do Kanban" (kanban, timeLane em minutos exibido com
// unidade, ao estourar = nextLaneId, lane de retorno = rollbackLaneId,
// mensagem ao mover + anexos mediaFiles) e Recorrência (recurrenceDays).
// Envio continua em multipart (POST/PUT /tags) por causa dos anexos.
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import Chip from "@material-ui/core/Chip";
import Popover from "@material-ui/core/Popover";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloseIcon from "@material-ui/icons/Close";
import ColorizeIcon from "@material-ui/icons/Colorize";
import ViewColumnIcon from "@material-ui/icons/ViewColumn";
import TimerIcon from "@material-ui/icons/Timer";
import EventRepeatIcon from "@material-ui/icons/Autorenew";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import { ColorBox } from "material-ui-color";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import useAerosSettingsStyles from "../Settings/aerosSettingsStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { SWATCHES, normalizeHex, tagChip, TIME_UNITS, minutesToUnit, unitToMinutes, RECURRENCE_OPTIONS } from "../../pages/Tags/tagRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const borderInput = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)";
  return {
    paper: { width: 560, maxWidth: "calc(100vw - 32px)", margin: 16, maxHeight: "calc(var(--vh-full) - 32px)" },
    nameRow: { display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "end" },
    preview: { height: 38, display: "flex", alignItems: "center", minWidth: 80 },
    chip: { display: "inline-flex", alignItems: "center", fontSize: 12, fontWeight: 600, borderRadius: 6, padding: "4px 12px", whiteSpace: "nowrap", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" },
    colorHead: { display: "flex", alignItems: "center", marginBottom: 8 },
    hex: {
      marginLeft: "auto",
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderInput}`,
      borderRadius: 8,
      padding: "3px 9px",
      fontSize: 11.5,
      fontFamily: "ui-monospace, monospace",
      color: theme.palette.text.secondary,
      display: "flex",
      alignItems: "center",
      gap: 6,
      "& input": { width: 66, background: "none", border: "none", color: "inherit", font: "inherit", outline: "none" },
    },
    hexDot: { width: 12, height: 12, borderRadius: 3 },
    swatches: { display: "flex", gap: 8, flexWrap: "wrap" },
    swatch: { width: 32, height: 32, borderRadius: 9, border: "2px solid transparent", boxSizing: "border-box", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#07080D", fontSize: 14, fontWeight: 800, padding: 0 },
    swatchOn: { borderColor: theme.palette.text.primary },
    swatchCustom: { width: 32, height: 32, borderRadius: 9, border: `1px dashed ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.25)"}`, background: "none", color: theme.palette.text.secondary, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", "& .MuiSvgIcon-root": { fontSize: 16 } },
    kanbanBox: { border: "1px solid rgba(160,68,255,0.3)", borderRadius: 12, overflow: "hidden", backgroundColor: "rgba(160,68,255,0.04)" },
    kanbanHead: { display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", "& > .MuiSvgIcon-root": { fontSize: 18, color: stateText(theme, "#C08BFF", "purple") } },
    boxTitle: { fontSize: 13, fontWeight: 600, color: theme.palette.text.primary },
    boxDesc: { fontSize: 11.5, color: theme.palette.text.secondary },
    kanbanGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, padding: "12px 14px", borderTop: "1px solid rgba(160,68,255,0.2)", [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" } },
    kanbanRow: { padding: "0 14px 12px" },
    smallLabel: { fontSize: 11.5, fontWeight: 600, color: theme.palette.text.secondary, marginBottom: 5, display: "block" },
    optional: { color: theme.palette.aeros.textTertiary, fontWeight: 500 },
    timeRow: { display: "flex", gap: 6, "& > :first-child": { flex: 1 } },
    warnIcon: { fontSize: 14, color: AEROS.warning, marginRight: 4 },
    recBox: { border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`, borderRadius: 12, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10, "& > .MuiSvgIcon-root": { fontSize: 18, color: theme.palette.text.secondary } },
    files: { display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 },
    fileChip: { fontSize: 11, height: 24, backgroundColor: alpha(AEROS.cyan, 0.08), color: theme.palette.text.primary },
    attach: { marginTop: 6 },
  };
});

const ALLOWED = ["image/jpeg", "image/png", "image/gif", "image/webp", "application/pdf", "audio/mpeg", "audio/wav", "audio/ogg", "video/mp4", "video/avi", "video/mov", "video/webm"];

const empty = {
  name: "",
  color: SWATCHES[0],
  kanban: 0,
  timeValue: "",
  timeUnit: "hours",
  nextLaneId: 0,
  rollbackLaneId: 0,
  greetingMessageLane: "",
  mediaFiles: [],
  recurrenceDays: 0,
};

const TagModal = ({ open, onClose, tagId, kanban, onSaved, onDelete }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const aeros = useAerosSettingsStyles();
  const [form, setForm] = useState(empty);
  const [tag, setTag] = useState(null);
  const [lanes, setLanes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pickerAnchor, setPickerAnchor] = useState(null);
  const [hexDraft, setHexDraft] = useState("");

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  useEffect(() => {
    if (!open) return;
    setPickerAnchor(null);
    api
      .get("/tags/", { params: { kanban: 1, tagId: tagId || 0, limit: "all" } })
      .then(({ data }) => setLanes(data.tags || []))
      .catch(() => setLanes([]));
    if (!tagId) {
      setTag(null);
      setForm({ ...empty, kanban: kanban ? 1 : 0, color: SWATCHES[Math.floor(Math.random() * SWATCHES.length)] });
      return;
    }
    api
      .get(`/tags/${tagId}`)
      .then(({ data }) => {
        let files = [];
        try {
          files = typeof data.mediaFiles === "string" ? JSON.parse(data.mediaFiles) : data.mediaFiles || [];
        } catch (e) {
          files = [];
        }
        const time = minutesToUnit(data.timeLane);
        setTag(data);
        setForm({
          name: data.name || "",
          color: normalizeHex(data.color) || SWATCHES[0],
          kanban: Number(data.kanban) === 1 ? 1 : 0,
          timeValue: time.value,
          timeUnit: time.unit,
          nextLaneId: data.nextLaneId || 0,
          rollbackLaneId: data.rollbackLaneId || 0,
          greetingMessageLane: data.greetingMessageLane || "",
          mediaFiles: (files || []).map((f) => ({ ...f, name: f.originalname || f.name || "arquivo", type: f.mimetype || f.type })),
          recurrenceDays: Number(data.recurrenceDays) || 0,
        });
      })
      .catch((err) => toastError(err));
  }, [open, tagId, kanban]);

  useEffect(() => {
    setHexDraft(form.color);
  }, [form.color]);

  const addFiles = (e) => {
    const files = Array.from(e.target.files || []).filter((f) => {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(i18n.t("tagModal.fileTooBig", { name: f.name }));
        return false;
      }
      if (!ALLOWED.includes(f.type)) {
        toast.error(i18n.t("tagModal.fileType", { type: f.type }));
        return false;
      }
      return true;
    });
    if (files.length) set({ mediaFiles: [...form.mediaFiles, ...files] });
    e.target.value = null;
  };

  const handleSave = async () => {
    if (!form.name || form.name.trim().length < 3) {
      toast.error(i18n.t("tagModal.nameShort"));
      return;
    }
    setSaving(true);
    const fd = new FormData();
    fd.append("name", form.name.trim());
    fd.append("color", form.color);
    fd.append("kanban", form.kanban);
    fd.append("timeLane", form.kanban ? unitToMinutes(form.timeValue, form.timeUnit) : 0);
    fd.append("nextLaneId", form.kanban ? form.nextLaneId || 0 : 0);
    fd.append("rollbackLaneId", form.kanban ? form.rollbackLaneId || 0 : 0);
    fd.append("greetingMessageLane", form.kanban ? form.greetingMessageLane || "" : "");
    fd.append("recurrenceDays", form.recurrenceDays || 0);
    form.mediaFiles.forEach((f) => {
      if (f instanceof File) fd.append("mediaFiles", f);
    });
    try {
      if (tagId) await api.put(`/tags/${tagId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      else await api.post("/tags", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(i18n.t("tagModal.success"));
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const chip = tagChip({ name: form.name || i18n.t("tagModal.previewEmpty"), color: form.color });
  const isEdit = Boolean(tagId);
  const otherLanes = lanes.filter((l) => l.id !== tagId);

  return (
    <Dialog open={Boolean(open)} onClose={saving ? undefined : onClose} maxWidth={false} classes={{ paper: classes.paper }} scroll="paper">
      <div className={m.form}>
        <div className={m.header}>
          <div>
            <div className={m.title}>{i18n.t(isEdit ? "tagModal.title.edit" : "tagModal.title.add")}</div>
            <div className={m.subtitle}>
              {isEdit && tag
                ? i18n.t("tagModal.usedIn", { contacts: tag.contacts?.length ?? tag.contactsCount ?? 0, tickets: tag.ticketsCount ?? tag.ticketTags?.length ?? 0 })
                : i18n.t("tagModal.subtitleAdd")}
            </div>
          </div>
          <IconButton className={m.closeButton} onClick={onClose} disabled={saving} aria-label={i18n.t("tagModal.buttons.cancel")}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className={m.body}>
          <div className={classes.nameRow}>
            <div>
              <label className={m.fieldLabel} htmlFor="tag-name">{i18n.t("tagModal.form.name")}</label>
              <TextField id="tag-name" variant="outlined" className={m.input} value={form.name} onChange={(e) => set({ name: e.target.value })} autoFocus placeholder={i18n.t("tagModal.form.namePlaceholder")} />
            </div>
            <div>
              <label className={m.fieldLabel}>{i18n.t("tagModal.form.preview")}</label>
              <div className={classes.preview}>
                <span className={classes.chip} style={{ color: chip.color, backgroundColor: chip.bg }}>{chip.label}</span>
              </div>
            </div>
          </div>

          <div>
            <div className={classes.colorHead}>
              <label className={m.fieldLabel} style={{ marginBottom: 0 }}>{i18n.t("tagModal.form.color")}</label>
              <div className={classes.hex}>
                <span className={classes.hexDot} style={{ backgroundColor: form.color }} />
                <input
                  value={hexDraft}
                  onChange={(e) => setHexDraft(e.target.value)}
                  onBlur={() => {
                    const hex = normalizeHex(hexDraft);
                    if (hex) set({ color: hex });
                    else setHexDraft(form.color);
                  }}
                  aria-label="Hex"
                />
              </div>
            </div>
            <div className={classes.swatches}>
              {SWATCHES.map((hex) => (
                <button
                  key={hex}
                  type="button"
                  className={`${classes.swatch} ${form.color === hex ? classes.swatchOn : ""}`}
                  style={{ backgroundColor: hex }}
                  onClick={() => set({ color: hex })}
                  aria-label={hex}
                  aria-pressed={form.color === hex}
                >
                  {form.color === hex ? "✓" : ""}
                </button>
              ))}
              <button type="button" className={classes.swatchCustom} onClick={(e) => setPickerAnchor(e.currentTarget)} aria-label={i18n.t("tagModal.form.customColor")}>
                <ColorizeIcon />
              </button>
              <Popover open={Boolean(pickerAnchor)} anchorEl={pickerAnchor} onClose={() => setPickerAnchor(null)} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
                <ColorBox
                  disableAlpha
                  hslGradient={false}
                  value={form.color}
                  onChange={(c) => {
                    const hex = normalizeHex(c?.hex ? `#${c.hex}` : c);
                    if (hex) set({ color: hex });
                  }}
                />
              </Popover>
            </div>
          </div>

          <div className={classes.kanbanBox}>
            <div className={classes.kanbanHead}>
              <ViewColumnIcon />
              <div style={{ flex: 1 }}>
                <div className={classes.boxTitle}>{i18n.t("tagModal.form.kanbanTitle")}</div>
                <div className={classes.boxDesc}>{i18n.t("tagModal.form.kanbanDesc")}</div>
              </div>
              <Switch className={aeros.toggleSwitch} checked={form.kanban === 1} onChange={(e) => set({ kanban: e.target.checked ? 1 : 0 })} color="primary" />
            </div>
            {form.kanban === 1 && (
              <>
                <div className={classes.kanbanGrid}>
                  <div>
                    <label className={classes.smallLabel}>{i18n.t("tagModal.form.timeLane")}</label>
                    <div className={classes.timeRow}>
                      <TextField
                        variant="outlined"
                        className={`${m.input} ${m.inputSmall}`}
                        type="number"
                        inputProps={{ min: 0 }}
                        value={form.timeValue}
                        onChange={(e) => set({ timeValue: e.target.value })}
                        placeholder="0"
                      />
                      <Select
                        variant="outlined"
                        className={`${m.input} ${m.inputSmall}`}
                        style={{ width: 110 }}
                        value={form.timeUnit}
                        onChange={(e) => set({ timeUnit: e.target.value })}
                        MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: "bottom", horizontal: "left" } }}
                      >
                        {TIME_UNITS.map((u) => (
                          <MenuItem key={u.id} value={u.id} dense>{u.label}</MenuItem>
                        ))}
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className={classes.smallLabel}>{i18n.t("tagModal.form.onTimeout")}</label>
                    <Select
                      variant="outlined"
                      fullWidth
                      displayEmpty
                      className={`${m.input} ${m.inputSmall}`}
                      value={form.nextLaneId || 0}
                      onChange={(e) => set({ nextLaneId: e.target.value })}
                      renderValue={(v) => {
                        const lane = otherLanes.find((l) => l.id === v);
                        return (
                          <>
                            <TimerIcon className={classes.warnIcon} />
                            {lane ? i18n.t("tagModal.form.moveTo", { lane: lane.name }) : i18n.t("tagModal.form.highlight")}
                          </>
                        );
                      }}
                      MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: "bottom", horizontal: "left" } }}
                    >
                      <MenuItem value={0} dense>{i18n.t("tagModal.form.highlight")}</MenuItem>
                      {otherLanes.map((l) => (
                        <MenuItem key={l.id} value={l.id} dense>{i18n.t("tagModal.form.moveTo", { lane: l.name })}</MenuItem>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label className={classes.smallLabel}>{i18n.t("tagModal.form.rollbackLaneId")}</label>
                    <Select
                      variant="outlined"
                      fullWidth
                      displayEmpty
                      className={`${m.input} ${m.inputSmall}`}
                      value={form.rollbackLaneId || 0}
                      onChange={(e) => set({ rollbackLaneId: e.target.value })}
                      MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: "bottom", horizontal: "left" } }}
                    >
                      <MenuItem value={0} dense>{i18n.t("tagModal.form.none")}</MenuItem>
                      {otherLanes.map((l) => (
                        <MenuItem key={l.id} value={l.id} dense>{l.name}</MenuItem>
                      ))}
                    </Select>
                  </div>
                </div>
                <div className={classes.kanbanRow}>
                  <label className={classes.smallLabel}>
                    {i18n.t("tagModal.form.greetingMessageLane")} <span className={classes.optional}>· {i18n.t("tagModal.form.optional")}</span>
                  </label>
                  <TextField
                    variant="outlined"
                    className={`${m.input} ${m.inputSmall}`}
                    multiline
                    rows={2}
                    value={form.greetingMessageLane}
                    onChange={(e) => set({ greetingMessageLane: e.target.value })}
                    placeholder={i18n.t("tagModal.form.greetingPlaceholder")}
                  />
                  <div className={classes.files}>
                    {form.mediaFiles.map((f, i) => (
                      <Chip
                        key={`${f.name}-${i}`}
                        size="small"
                        className={classes.fileChip}
                        icon={<AttachFileIcon style={{ fontSize: 14 }} />}
                        label={f.name}
                        onDelete={() => set({ mediaFiles: form.mediaFiles.filter((_, j) => j !== i) })}
                      />
                    ))}
                  </div>
                  <button type="button" className={`${m.linkButton} ${classes.attach}`} style={{ marginLeft: 0 }} onClick={() => document.getElementById("tag-media-upload").click()}>
                    <AttachFileIcon /> {i18n.t("tagModal.form.attach")}
                  </button>
                  <input id="tag-media-upload" type="file" multiple accept="image/*,application/pdf,audio/*,video/*" style={{ display: "none" }} onChange={addFiles} />
                </div>
              </>
            )}
          </div>

          <div className={classes.recBox}>
            <EventRepeatIcon />
            <div style={{ flex: 1 }}>
              <div className={classes.boxTitle}>{i18n.t("tagModal.form.recurrence")}</div>
              <div className={classes.boxDesc}>{i18n.t("tagModal.form.recurrenceDesc")}</div>
            </div>
            <Select
              variant="outlined"
              className={`${m.input} ${m.inputSmall}`}
              style={{ width: 130 }}
              value={form.recurrenceDays || 0}
              onChange={(e) => set({ recurrenceDays: e.target.value })}
              MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: "bottom", horizontal: "left" } }}
            >
              {RECURRENCE_OPTIONS.map((o) => (
                <MenuItem key={o.days} value={o.days} dense>{o.label}</MenuItem>
              ))}
            </Select>
          </div>
        </div>

        <div className={m.footer}>
          {isEdit && onDelete && (
            <button type="button" className={m.dangerLink} onClick={() => onDelete({ ...tag, ...form, id: tagId })}>
              <DeleteOutlineIcon /> {i18n.t("tagModal.buttons.delete")}
            </button>
          )}
          <div className={m.footerActions}>
            <Button variant="outlined" className={m.cancelButton} onClick={onClose} disabled={saving}>
              {i18n.t("tagModal.buttons.cancel")}
            </Button>
            <Button variant="contained" color="primary" className={m.submitButton} onClick={handleSave} disabled={saving}>
              {i18n.t(isEdit ? "tagModal.buttons.okEdit" : "tagModal.buttons.okAdd")}
              {saving && <CircularProgress size={22} className={m.buttonProgress} />}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default TagModal;
